/**
 * Payments API Client
 * Handles all payment operations for EV charging sessions
 * Integrates with backend payment endpoints
 */

import axios from 'axios';
import { v4 as uuidv4 } from './uuid.js';

const API_BASE = import.meta.env.VITE_API_URL;

/**
 * Generate or retrieve idempotency key for a specific operation
 * Keys are stored in memory per session to enable retries
 */
const idempotencyKeys = new Map();

function getIdempotencyKey(operation, sessionId, extra = '') {
  const key = `${operation}:${sessionId}${extra ? `:${extra}` : ''}`;
  
  if (!idempotencyKeys.has(key)) {
    idempotencyKeys.set(key, uuidv4());
  }
  
  return idempotencyKeys.get(key);
}

/**
 * Clear idempotency keys for a session (call when session completes)
 */
export function clearSessionIdempotencyKeys(sessionId) {
  const keysToDelete = [];
  for (const [key] of idempotencyKeys) {
    if (key.includes(sessionId)) {
      keysToDelete.push(key);
    }
  }
  keysToDelete.forEach(key => idempotencyKeys.delete(key));
}

/**
 * Generate correlation ID for request tracking
 */
function getCorrelationId() {
  return `ev-user-${Date.now()}-${Math.random().toString(36).substring(7)}`;
}

/**
 * Pre-authorize payment for charging session
 * @param {string} sessionId - EV session ID
 * @param {object} params - Payment parameters
 * @param {number} params.amount - Amount in dollars (will be sent as-is)
 * @param {number} params.currency - Currency numeric code (default 840 for USD)
 * @param {object} params.paymentMethod - Payment method details
 * @param {object} params.paymentMethod.card - Card details
 * @param {string} params.paymentMethod.card.number - Card number
 * @param {string} params.paymentMethod.card.expMonth - Expiration month (MM)
 * @param {string} params.paymentMethod.card.expYear - Expiration year (YYYY)
 * @param {string} params.paymentMethod.card.cvv - CVV code
 * @param {object} [params.paymentMethod.billing] - Billing details (optional)
 * @returns {Promise<object>} Response with payment authorization result
 */
export async function preauthPayment(sessionId, { amount, currency = 52, paymentMethod }) {
  const correlationId = getCorrelationId();
  const idempotencyKey = getIdempotencyKey('preauth', sessionId);

  try {
    console.log('[Payments] Pre-authorizing payment for session:', sessionId);
    
    const response = await axios.post(
      `${API_BASE}/payments/sessions/${sessionId}/payment/preauth`,
      {
        amount,
        currency,
        paymentMethod
      },
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[Payments] Preauth successful:', {
      status: response.data.payment?.status,
      correlationId: response.data.correlationId
    });

    return {
      ok: true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[Payments] Preauth failed:', error.response?.data || error.message);
    
    return {
      ok: false,
      error: error.response?.data?.error || 'PREAUTH_FAILED',
      message: error.response?.data?.message || error.message,
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Capture payment after charging session completes
 * @param {string} sessionId - EV session ID
 * @param {object} params - Capture parameters
 * @param {number} params.amount - Actual amount to capture in dollars
 * @returns {Promise<object>} Response with capture result
 */
export async function capturePayment(sessionId, { amount }) {
  const correlationId = getCorrelationId();
  const idempotencyKey = getIdempotencyKey('capture', sessionId, String(amount));

  try {
    console.log('[Payments] Capturing payment for session:', sessionId, 'amount:', amount);
    
    const response = await axios.post(
      `${API_BASE}/payments/sessions/${sessionId}/payment/capture`,
      { amount },
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[Payments] Capture successful:', {
      status: response.data.payment?.status,
      correlationId: response.data.correlationId
    });

    return {
      ok: true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[Payments] Capture failed:', error.response?.data || error.message);
    
    return {
      ok: false,
      error: error.response?.data?.error || 'CAPTURE_FAILED',
      message: error.response?.data?.message || error.message,
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Void pre-authorization (cancel before charging starts)
 * @param {string} sessionId - EV session ID
 * @param {object} params - Void parameters
 * @param {string} [params.reason] - Reason for voiding
 * @returns {Promise<object>} Response with void result
 */
export async function voidPayment(sessionId, { reason } = {}) {
  const correlationId = getCorrelationId();
  const idempotencyKey = getIdempotencyKey('void', sessionId);

  try {
    console.log('[Payments] Voiding payment for session:', sessionId);
    
    const response = await axios.post(
      `${API_BASE}/payments/sessions/${sessionId}/payment/void`,
      { reason },
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[Payments] Void successful');

    return {
      ok: true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[Payments] Void failed:', error.response?.data || error.message);
    
    return {
      ok: false,
      error: error.response?.data?.error || 'VOID_FAILED',
      message: error.response?.data?.message || error.message,
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Refund captured payment (optional - for admin/support use)
 * @param {string} sessionId - EV session ID
 * @param {object} params - Refund parameters
 * @param {number} params.amount - Amount to refund in dollars
 * @param {string} [params.reason] - Reason for refund
 * @returns {Promise<object>} Response with refund result
 */
export async function refundPayment(sessionId, { amount, reason } = {}) {
  const correlationId = getCorrelationId();
  const idempotencyKey = getIdempotencyKey('refund', sessionId, String(amount));

  try {
    console.log('[Payments] Refunding payment for session:', sessionId, 'amount:', amount);
    
    const response = await axios.post(
      `${API_BASE}/payments/sessions/${sessionId}/payment/refund`,
      { amount, reason },
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[Payments] Refund successful');

    return {
      ok: true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[Payments] Refund failed:', error.response?.data || error.message);
    
    return {
      ok: false,
      error: error.response?.data?.error || 'REFUND_FAILED',
      message: error.response?.data?.message || error.message,
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}

/**
 * Tokenize card for future use (optional)
 * @param {object} params - Tokenization parameters
 * @param {object} params.card - Card details
 * @param {object} [params.billing] - Billing details
 * @returns {Promise<object>} Response with token
 */
export async function tokenizeCard({ card, billing } = {}) {
  const correlationId = getCorrelationId();
  const cardLast4 = card?.number?.replace(/\s/g, '').slice(-4) || 'unknown';
  const idempotencyKey = getIdempotencyKey('tokenize', cardLast4);

  try {
    console.log('[Payments] Tokenizing card');
    
    const response = await axios.post(
      `${API_BASE}/payments/payment/tokenize`,
      { card, billing },
      {
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          'X-Correlation-Id': correlationId
        }
      }
    );

    console.log('[Payments] Tokenization successful');

    return {
      ok: true,
      ...response.data,
      correlationId
    };
  } catch (error) {
    console.error('[Payments] Tokenization failed:', error.response?.data || error.message);
    
    return {
      ok: false,
      error: error.response?.data?.error || 'TOKENIZE_FAILED',
      message: error.response?.data?.message || error.message,
      correlationId: error.response?.data?.correlationId || correlationId
    };
  }
}
